'use client';

import React, { useState } from 'react';
import { clsx } from 'clsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@src/components/ui/dropdown-menu';
import Icon from '@src/v3/components/coreUI/icon';
import type { FilterDropdownProps } from '@src/v3/interfaces/components/coreUI/filterDropdown';
import Button from '@src/v3/components/coreUI/button';

// Helper function to get icon props based on icon name
const getIconProps = (iconName: string) => {
  switch (iconName) {
    case 'size':
    case 'squareFootage':
      return {
        width: '16',
        height: '16',
        viewBox: '0 0 16 16',
        stroke: 'currentColor',
        strokeWidth: '1.5',
        strokeLinecap: 'round' as const,
        strokeLinejoin: 'round' as const,
        useNewPaths: true,
      };
    case 'bed':
      return {
        width: '16',
        height: '16',
        viewBox: '0 0 17 16',
        stroke: 'currentColor',
        strokeWidth: '1.5',
        strokeLinecap: 'round' as const,
        strokeLinejoin: 'round' as const,
        useNewPaths: true,
      };
    case 'bath':
      return {
        width: '16',
        height: '16',
        viewBox: '0 0 16 16',
        stroke: 'currentColor',
        strokeWidth: '1.5',
        strokeLinecap: 'round' as const,
        strokeLinejoin: 'round' as const,
        useNewPaths: true,
      };
    default:
      return {};
  }
};

const FilterDropdown = ({
  label,
  iconName,
  content,
  dropdownWidth = 'w-[300px]',
  buttonClassName = '',
  disabled = false,
  onOpenChange,
  onApplyFilter,
  onResetFilter,
  open,
  align = 'start',
  isActive = false,
  showFooter = true,
  footerBtnText = 'Show homes',
  footerBtnDisabled = false,
}: FilterDropdownProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Use controlled state if open prop is provided, otherwise use internal state
  const dropdownOpen = open !== undefined ? open : isOpen;

  const handleOpenChange = (newOpen: boolean) => {
    if (open === undefined) {
      setIsOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  const handleApplyFilter = () => {
    onApplyFilter();
    handleOpenChange(false);
  };

  const handleResetFilter = () => {
    onResetFilter?.();
    handleOpenChange(false);
  };
  const buttonClasses = clsx(
    'h-14 p-4 flex items-center bg-gray-lighter hover:bg-gray-light rounded-full !gap-x-3 text-base text-gray-dark hover:text-gray-dark font-semibold transition-colors border-2',
    {
      'opacity-50 cursor-not-allowed': disabled,
      'border-primary-dark text-black-dark/90 hover:text-black-dark/90 hover:bg-gray-lighter':
        isActive,
      'border-transparent': !isActive,
    },
    buttonClassName
  );

  const contentClassName = 'bg-white rounded-3xl border-0 shadow-lg';

  const contentClasses = clsx(dropdownWidth, contentClassName);

  return (
    <DropdownMenu
      open={dropdownOpen}
      onOpenChange={disabled ? undefined : handleOpenChange}
    >
      <DropdownMenuTrigger asChild>
        <div className="relative pt-2">
          <Button variant="ghost" className={buttonClasses} disabled={disabled}>
            {iconName ? (
              <Icon
                iconName={iconName}
                iconProps={{
                  className: 'w-6 h-6',
                  ...getIconProps(iconName),
                }}
              />
            ) : null}
            {label}
            <Icon iconName="chevronDown" iconProps={{ className: 'w-3 h-3' }} />
          </Button>
          {isActive && (
            <div className="absolute top-1 right-0 w-5 h-5 border-2 border-white bg-primary-dark rounded-full flex items-center justify-center z-10">
              {/* <span className="text-white text-base font-semibold">1</span> */}
            </div>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className={contentClasses}
        sideOffset={8}
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 p-8 overflow-auto">{content}</div>

          {/* Footer - Sticky at bottom */}
          {showFooter && (
            <div className="sticky bottom-0 mt-auto border-t border-gray-200 px-8 py-6 bg-white rounded-b-3xl z-10 flex gap-x-6 min-w-0">
              <Button
                variant="ghost"
                size="lg"
                className="text-sm font-medium p-0 hover:bg-transparent underline min-w-0"
                onClick={handleResetFilter}
              >
                Reset Filters
              </Button>
              <Button
                variant="primary"
                size="lg"
                className="w-full text-base font-medium flex-1 min-w-0"
                onClick={handleApplyFilter}
                disabled={!isActive && footerBtnDisabled}
              >
                {footerBtnText}
              </Button>
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default FilterDropdown;
